type PostHog = typeof import("posthog-js").default;

// The real SDK is ~180KB and dominates the site's main-thread cost if it
// ships in the synchronous pre-hydration bundle (a Lighthouse mobile audit
// attributed ~600ms of LCP and 626ms of blocking CPU time to it). A dynamic
// import() here is fire-and-forget per Next's instrumentation-client
// contract (async work isn't awaited before hydration) — fine for
// analytics, unlike a polyfill — and lets the bundler split it into its own
// chunk instead of inlining it into every route's critical path.
let posthog: PostHog | undefined;
const pendingEvents: { event: string; properties?: Record<string, unknown> }[] = [];

// Client-side product analytics (PostHog). Loaded once from
// instrumentation-client.ts, before hydration. No key → no-op (local dev
// default), mirroring the Sentry setup.
export async function initAnalytics() {
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  const mod = await import("posthog-js");
  posthog = mod.default;

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
    defaults: "2026-05-30",
    // Person profiles only for signed-in (identified) users; anonymous
    // marketing/taster traffic stays event-only.
    person_profiles: "identified_only",
    // Learners (many children) write personal text in this app, and the free
    // writing check promises the text never leaves the browser. Replay masks
    // every input; rendered writing is masked with the `ph-mask` class (on the
    // app's <main> and the public tools' results). See decisions.md, 2026-11-03.
    session_recording: {
      maskAllInputs: true,
    },
    // Autocapture copies a clicked element's text into $el_text, which picked up
    // learner writing on the mark page. Tag and href still identify the click.
    mask_all_text: true,
    // Client-side error monitoring. posthog-js loads its exception-autocapture
    // bundle and sends $exception events for unhandled errors and unhandled
    // promise rejections. This replaces Sentry's browser SDK.
    capture_exceptions: true,
  });

  // One project across environments — filter by this super property.
  posthog.register({
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV ?? "development",
  });

  await wireIdentity(posthog);

  // Flush anything fired between page load and the dynamic import
  // resolving, in order, so deferring init never drops an event.
  for (const { event, properties } of pendingEvents) {
    posthog.capture(event, properties);
  }
  pendingEvents.length = 0;
}

// Fire a named product event. Guarded the same way initAnalytics is (no key
// → no-op) since this can be called from routes that render even when
// PostHog was never initialised (e.g. local dev with no key set). Queues
// until the deferred SDK import resolves rather than dropping the event.
export function track(event: string, properties?: Record<string, unknown>) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  if (!posthog) {
    pendingEvents.push({ event, properties });
    return;
  }
  posthog.capture(event, properties);
}

// Tie events to the Supabase user across all auth flows (password login,
// signup, PKCE callback) without touching each page: identify whenever a
// session exists, reset when it ends so the next visitor isn't linked to
// the previous account. Dynamically imported for the same reason posthog-js
// is (see above) — @/lib/supabase pulls in the full Supabase JS SDK, and a
// static import here would put it right back on the critical path.
async function wireIdentity(posthog: PostHog) {
  const { createClient } = await import("@/lib/supabase");
  const supabase = createClient();
  supabase.auth.onAuthStateChange((event, session) => {
    if (session?.user && posthog.get_distinct_id() !== session.user.id) {
      posthog.identify(session.user.id, { email: session.user.email });
    } else if (event === "SIGNED_OUT") {
      posthog.reset();
    }
  });
}
